import Box from '@mui/material/Box'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import { noop, isEmpty } from 'lodash'
import React, { useMemo, useEffect, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { useLazyGetServiceDetailsQuery } from 'api/configured-service/getServiceDetails'
import { useLazyGetDisposalFeesLinkedToConfiguredServiceQuery } from 'api/disposal-fee/disposalFeeServicesCrud'
import { useGetDisposalSitesMutation } from 'api/settings/getDisposalSites'
import { useLazyFetchWorkOrdersDisposalTicketQuery } from 'api/work-order/getWorkOrdersDisposalTicket'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import Loader from 'components/common/loader'
import { HHFormSwitchField } from 'components/form-fields/v5'
import DisposalTicketTaxInfoIcon from 'components/work-order/details/disposal-ticket/DisposalTicketAccordion/DisposalTicketTaxInfoIcon'
import {
  getTransformDisposalData,
  getTransformDisposalTicket,
  getIsTaxableFromLocalStorage,
  setIsTaxableInLocalStorage,
} from 'components/work-order/details/disposal-ticket/settings'
import { selectIsNewTaxesEnabled } from 'data/tenant/tenantSettingsSelectors'
import T from 'T'
import { createDateFromString } from 'utils/date'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import BasicFields from './BasicFields'
import DialogFooter from './DialogFooter'
import DisposalFee from './disposal-fee'
import MaterialField from './MaterialField'
import { FORM_FIELDS } from './settings'

const MAX_CONTENT_HEIGHT = 'calc(100vh - 260px)'

type DisposalTicket = {
  id: string
  [field: string]: unknown
}

// FORM_FIELDS (JS) infers effectiveDate as `null`; the transforms also add the
// source workOrderId when editing an existing ticket.
type DisposalTicketFormValues = Omit<typeof FORM_FIELDS, 'effectiveDate'> & {
  effectiveDate: Date | null
  workOrderId?: string
}

type DisposalTicketDialogProps = {
  isLocked?: boolean
  isOpen?: boolean
  workOrderId: string
  configuredServiceId: string
  serviceDate: string
  /** Multi-ticket callers: '' adds a new ticket, an id edits that ticket; undefined = legacy single-ticket behavior. */
  disposalTicketId?: string
  onSuccess?: () => void
  onClose?: () => void
}

const DisposalTicketDialog: React.FC<DisposalTicketDialogProps> = ({
  isLocked = false,
  isOpen = false,
  workOrderId,
  configuredServiceId,
  serviceDate,
  disposalTicketId,
  onSuccess = noop,
  onClose = noop,
}) => {
  const [getDisposalSites, { data: disposalSites, isLoading: isGetDisposalSitesLoading }] = useGetDisposalSitesMutation()
  const [getWorkOrdersDisposalTicket, { isFetching: isGetWorkOrdersDisposalTicketLoading }] = useLazyFetchWorkOrdersDisposalTicketQuery()
  const [getDisposalFeesLinkedToConfiguredService, { isFetching: isGetDisposalFeesLinkedToCSFetching, data: disposalFeesLinkedToCS = [] }] =
    useLazyGetDisposalFeesLinkedToConfiguredServiceQuery()
  const [getServiceDetails, { data: serviceData, isFetching: isGetServiceDetailsLoading }] = useLazyGetServiceDetailsQuery()

  const disposalTicketForm = useForm<DisposalTicketFormValues>({ defaultValues: { ...FORM_FIELDS } })
  const { control, setFocus, reset, setValue, watch } = disposalTicketForm
  const isNewTaxesEnabled = useSelector(selectIsNewTaxesEnabled)
  const taxable = watch('taxable')

  // When the new-tax pipeline is enabled for this tenant, the legacy per-ticket
  // taxable toggle is retired — force the form value to false so downstream fee
  // math / submit stay consistent with the hidden UI.
  useEffect(() => {
    if (isNewTaxesEnabled && taxable !== false) {
      setValue('taxable', false)
    }
  }, [isNewTaxesEnabled, taxable, setValue])

  const allSites = useMemo(() => get(disposalSites, 'sites', []), [disposalSites])
  const isGetLoading = useMemo(
    () =>
      isGetDisposalSitesLoading ||
      isGetWorkOrdersDisposalTicketLoading ||
      isGetDisposalFeesLinkedToCSFetching ||
      isGetServiceDetailsLoading,
    [isGetDisposalSitesLoading, isGetWorkOrdersDisposalTicketLoading, isGetDisposalFeesLinkedToCSFetching, isGetServiceDetailsLoading]
  )
  const disposalFees = useMemo(
    () => disposalFeesLinkedToCS.map((disposalFee: unknown) => get(disposalFee, 'materials', [])).flat(),
    [disposalFeesLinkedToCS]
  )

  const handleClose = useCallback(() => {
    onClose()
    reset({ ...FORM_FIELDS })
  }, [onClose, reset])

  const handleIsTaxableChange = useCallback((value: boolean) => {
    setIsTaxableInLocalStorage(value)
  }, [])

  const fetchDisposalTicketData = async () => {
    const disposalSitesResponse = await getDisposalSites(undefined).unwrap().catch(handleError)
    const defaultSite = get(disposalSitesResponse, 'sites', []).find((site: { default?: boolean }) => site.default)

    const resetToNewTicket = () =>
      reset({
        ...FORM_FIELDS,
        disposalSiteId: get(defaultSite, 'id', ''),
        effectiveDate: createDateFromString(serviceDate),
        taxable: getIsTaxableFromLocalStorage(),
      })

    getWorkOrdersDisposalTicket(workOrderId)
      .unwrap()
      .then((response: DisposalTicket[] | undefined) => {
        setFocus('ticketNumber')

        if (disposalTicketId !== undefined) {
          const ticketToEdit = disposalTicketId ? (response || []).find(ticket => get(ticket, 'id') === disposalTicketId) : null
          if (ticketToEdit) {
            reset({ ...getTransformDisposalTicket(ticketToEdit) })
          } else {
            resetToNewTicket()
          }
          return
        }

        // Legacy single-ticket callers (no disposalTicketId prop): edit the WO's first ticket if any.
        if (isEmpty(response)) {
          resetToNewTicket()
          return
        }
        reset({ ...getTransformDisposalData(response) })
      })
      .catch(handleError)
  }

  useEffect(() => {
    if (isOpen) {
      getServiceDetails(configuredServiceId)
      getDisposalFeesLinkedToConfiguredService({ id: configuredServiceId, includeArchived: false }).unwrap().catch(handleError)
      fetchDisposalTicketData()
    }
    // Intentionally runs only when the dialog opens or the target ticket changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, disposalTicketId])

  return (
    <>
      {isGetLoading && isOpen && <Loader />}
      <HHBaseDialog fullWidth maxWidth="xl" open={isOpen} onClose={handleClose}>
        <FormProvider {...disposalTicketForm}>
          <HHDialogTitle stackSx={{ display: 'flex' }} title={T.DISPOSAL_TICKET} onClose={handleClose}>
            <Box alignItems="center" display="flex" gap={1} justifyContent="flex-end">
              {!isNewTaxesEnabled && (
                <>
                  <HHFormSwitchField control={control} label={T.TAXABLE} name="taxable" onChange={handleIsTaxableChange} />
                  <DisposalTicketTaxInfoIcon />
                </>
              )}
            </Box>
          </HHDialogTitle>
          <DialogContent sx={{ overflow: { md: 'hidden' } }}>
            <Grid container spacing={2}>
              <Grid container height="100%" overflow="auto" size={{ xs: 12, md: 6 }} sx={{ maxHeight: { md: MAX_CONTENT_HEIGHT } }}>
                <BasicFields allSites={allSites} />
                <Grid size={12}>
                  <MaterialField allSites={allSites} disposalFees={disposalFees} />
                </Grid>
              </Grid>
              <Grid overflow="auto" size={{ xs: 12, md: 6 }} sx={{ maxHeight: { md: MAX_CONTENT_HEIGHT } }}>
                <DisposalFee allSites={allSites} serviceData={serviceData} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogFooter isLocked={isLocked} workOrderId={workOrderId} onClose={handleClose} onSuccess={onSuccess} />
        </FormProvider>
      </HHBaseDialog>
    </>
  )
}

export default DisposalTicketDialog
